import { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { X, Heart, Flame, Zap, Star, ArrowRight, RotateCcw } from "lucide-react";

const QUIZ_QUESTIONS = [
  {
    id: 1,
    question: "When you first met this person, what did you feel?",
    options: [
      { text: "Instant, overwhelming recognition — like coming home", score: { twinFlame: 3, soulmate: 1, karmic: 0 } },
      { text: "Comfortable and familiar, like an old friend", score: { twinFlame: 0, soulmate: 3, karmic: 1 } },
      { text: "Intense attraction that felt almost magnetic", score: { twinFlame: 1, soulmate: 0, karmic: 3 } },
      { text: "Something felt 'off' but I couldn't look away", score: { twinFlame: 0, soulmate: 0, karmic: 2 } }
    ]
  },
  {
    id: 2,
    question: "How does this relationship affect your personal growth?",
    options: [
      { text: "It's forced me to confront ALL my deepest wounds", score: { twinFlame: 3, soulmate: 1, karmic: 1 } },
      { text: "We grow together in a supportive, balanced way", score: { twinFlame: 1, soulmate: 3, karmic: 0 } },
      { text: "The same patterns keep repeating no matter what", score: { twinFlame: 0, soulmate: 0, karmic: 3 } },
      { text: "I feel like I'm losing myself in this connection", score: { twinFlame: 0, soulmate: 0, karmic: 2 } }
    ]
  },
  {
    id: 3,
    question: "When you're apart from this person, how do you feel?",
    options: [
      { text: "I can literally feel them energetically, even at a distance", score: { twinFlame: 3, soulmate: 0, karmic: 1 } },
      { text: "I miss them but I'm at peace and continue living fully", score: { twinFlame: 1, soulmate: 3, karmic: 0 } },
      { text: "Anxious, obsessive, can't stop thinking about them", score: { twinFlame: 1, soulmate: 0, karmic: 3 } },
      { text: "Relief mixed with longing — it's confusing", score: { twinFlame: 0, soulmate: 1, karmic: 2 } }
    ]
  },
  {
    id: 4,
    question: "What patterns have emerged in this relationship?",
    options: [
      { text: "Runner/chaser dynamics with periods of separation", score: { twinFlame: 3, soulmate: 0, karmic: 1 } },
      { text: "Stable commitment with natural ebb and flow", score: { twinFlame: 0, soulmate: 3, karmic: 0 } },
      { text: "On-again, off-again — same fights, different day", score: { twinFlame: 0, soulmate: 0, karmic: 3 } },
      { text: "Drama and chaos seem to follow us everywhere", score: { twinFlame: 0, soulmate: 0, karmic: 3 } }
    ]
  },
  {
    id: 5,
    question: "How has this connection affected your spiritual journey?",
    options: [
      { text: "Complete spiritual awakening — everything changed", score: { twinFlame: 3, soulmate: 1, karmic: 0 } },
      { text: "Supported my existing spiritual growth", score: { twinFlame: 1, soulmate: 3, karmic: 0 } },
      { text: "Made me question everything about myself", score: { twinFlame: 2, soulmate: 0, karmic: 2 } },
      { text: "Honestly, more focused on the relationship drama", score: { twinFlame: 0, soulmate: 0, karmic: 3 } }
    ]
  },
  {
    id: 6,
    question: "When this person triggers you, what happens?",
    options: [
      { text: "My deepest childhood wounds come flooding up", score: { twinFlame: 3, soulmate: 1, karmic: 2 } },
      { text: "We can talk through it and both learn", score: { twinFlame: 1, soulmate: 3, karmic: 0 } },
      { text: "Same argument, different day — nothing changes", score: { twinFlame: 0, soulmate: 0, karmic: 3 } },
      { text: "I find myself becoming someone I don't recognize", score: { twinFlame: 0, soulmate: 0, karmic: 2 } }
    ]
  },
  {
    id: 7,
    question: "How do friends and family view this relationship?",
    options: [
      { text: "They don't fully understand but see my transformation", score: { twinFlame: 3, soulmate: 1, karmic: 0 } },
      { text: "They love this person and support us completely", score: { twinFlame: 1, soulmate: 3, karmic: 0 } },
      { text: "They're concerned and keep telling me to leave", score: { twinFlame: 0, soulmate: 0, karmic: 3 } },
      { text: "They've stopped talking about it — they're exhausted", score: { twinFlame: 0, soulmate: 0, karmic: 2 } }
    ]
  },
  {
    id: 8,
    question: "What do you ultimately believe about this connection?",
    options: [
      { text: "We're two halves of the same soul — nothing can break that", score: { twinFlame: 3, soulmate: 0, karmic: 0 } },
      { text: "We're soul family who chose to journey together", score: { twinFlame: 0, soulmate: 3, karmic: 1 } },
      { text: "There's unfinished business we need to resolve", score: { twinFlame: 1, soulmate: 1, karmic: 3 } },
      { text: "I'm not sure anymore — it's all very confusing", score: { twinFlame: 1, soulmate: 1, karmic: 2 } }
    ]
  }
];

const RESULTS = {
  twinFlame: {
    title: "Twin Flame Connection",
    emoji: "🔥",
    color: "from-rose-500 to-orange-500",
    description: `Based on your answers, this appears to be a Twin Flame connection.

Twin flames are the other half of your soul — one soul split into two bodies. This journey is primarily about spiritual awakening, not just romance.

🔥 KEY CHARACTERISTICS OF YOUR CONNECTION:
• Intense soul recognition and telepathic bond
• Triggers deep healing and transformation  
• May involve runner/chaser dynamics
• Forces you to face ALL your shadows
• Leads to spiritual awakening

💫 WHAT THIS MEANS FOR YOU:
The twin flame journey is about becoming whole within yourself. Union with your twin happens when you achieve inner union first — balancing your own divine masculine and feminine.

Focus on YOUR healing. The connection will evolve as you do.`,
    advice: "Read 'Is It a Blessing or a Curse?' and 'Understanding Divine Partnerships' in our Readables library."
  },
  soulmate: {
    title: "Soulmate Connection",
    emoji: "💕",
    color: "from-pink-500 to-rose-500",
    description: `Based on your answers, this appears to be a Soulmate connection.

Soulmates are souls from your soul family — beings you've traveled with across many lifetimes. Unlike twin flames, you have MANY soulmates!

💕 KEY CHARACTERISTICS OF YOUR CONNECTION:
• Feels comfortable and familiar
• Mutual growth and support
• Balanced give-and-take energy
• Can build a stable, loving life together
• Growth happens alongside, not through, pain

💫 WHAT THIS MEANS FOR YOU:
You can absolutely build a beautiful, lasting love with a soulmate. This connection supports your growth while providing stability and companionship.

Honor this bond. Not everyone needs the intensity of a twin flame journey — soulmate love is equally divine.`,
    advice: "Read 'Recognizing Your Soulmate' and 'Divine Feminine & Masculine' in our Readables library."
  },
  karmic: {
    title: "Karmic Connection",
    emoji: "⚡",
    color: "from-purple-500 to-indigo-500",
    description: `Based on your answers, this appears to be a Karmic connection.

Karmic partners are souls you have unfinished business with from past lives. You've agreed to meet again to learn lessons or balance karma.

⚡ KEY CHARACTERISTICS OF YOUR CONNECTION:
• Intense but often toxic patterns
• Same issues keep repeating
• Feels addictive or hard to leave
• May bring out your worst traits
• Friends/family are concerned

💫 WHAT THIS MEANS FOR YOU:
Karmic connections aren't punishment — they're opportunities to learn and grow. BUT once the lesson is learned, you're free to move on.

Ask yourself: What is this relationship teaching me? What pattern am I being asked to break?

Sometimes the lesson IS learning to leave.`,
    advice: "Read 'Soulmate or Karmic?' in our Readables library to understand how to complete this cycle."
  }
};

const SoulConnectionQuiz = ({ isOpen, onClose, onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [scores, setScores] = useState({ twinFlame: 0, soulmate: 0, karmic: 0 });
  const [showResults, setShowResults] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  const handleAnswer = (option) => {
    setSelectedOption(option);
    
    // Update scores
    const newScores = {
      twinFlame: scores.twinFlame + option.score.twinFlame,
      soulmate: scores.soulmate + option.score.soulmate,
      karmic: scores.karmic + option.score.karmic
    };
    setScores(newScores);
    
    // Move to next question or show results
    setTimeout(() => {
      if (currentQuestion < QUIZ_QUESTIONS.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedOption(null);
      } else {
        setShowResults(true);
        // Calculate result
        const maxScore = Math.max(newScores.twinFlame, newScores.soulmate, newScores.karmic);
        let resultType = 'karmic';
        if (newScores.twinFlame === maxScore) resultType = 'twinFlame';
        else if (newScores.soulmate === maxScore) resultType = 'soulmate';
        
        const quizResult = {
          type: resultType,
          title: RESULTS[resultType].title,
          emoji: RESULTS[resultType].emoji,
          description: RESULTS[resultType].description.split('\n')[0], // First line only for preview
          scores: newScores,
          takenAt: new Date().toISOString()
        };
        
        // Save to localStorage for immediate use
        localStorage.setItem('soul_connection_quiz_result', JSON.stringify(quizResult));
        
        // Save to database for persistence across sessions (using .then() instead of await)
        const token = localStorage.getItem('token');
        if (token) {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          fetch(`${API_URL}/api/soul-connection-quiz/save`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              type: resultType,
              title: RESULTS[resultType].title,
              emoji: RESULTS[resultType].emoji,
              description: RESULTS[resultType].description.split('\n')[0],
              scores: newScores
            })
          }).catch(err => {
            console.error('Failed to save quiz result to database:', err);
          });
        }
        
        // Callback to parent if provided
        if (onComplete) onComplete(quizResult);
      }
    }, 500);
  };

  const getResult = () => {
    const maxScore = Math.max(scores.twinFlame, scores.soulmate, scores.karmic);
    if (scores.twinFlame === maxScore) return RESULTS.twinFlame;
    if (scores.soulmate === maxScore) return RESULTS.soulmate;
    return RESULTS.karmic;
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setScores({ twinFlame: 0, soulmate: 0, karmic: 0 });
    setShowResults(false);
    setSelectedOption(null);
  };

  if (!isOpen) return null;

  const progress = ((currentQuestion + 1) / QUIZ_QUESTIONS.length) * 100;
  const result = showResults ? getResult() : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <Card className="w-full sm:max-w-lg max-h-[85vh] sm:max-h-[90vh] overflow-hidden rounded-t-3xl sm:rounded-xl" onClick={e => e.stopPropagation()}>
        <CardContent className="p-0">
          {/* Header */}
          <div className={`sticky top-0 bg-gradient-to-r ${showResults ? result.color : 'from-violet-500 to-pink-500'} p-4 flex items-center justify-between`}>
            <div className="flex items-center gap-2 text-white">
              {showResults ? (
                <span className="text-2xl">{result.emoji}</span>
              ) : (
                <Heart className="h-6 w-6" />
              )}
              <h2 className="text-lg font-bold">
                {showResults ? 'Your Result' : 'Soul Connection Quiz'}
              </h2>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="h-5 w-5" />
            </Button>
          </div>

          {!showResults ? (
            /* Questions */
            <div className="p-4 sm:p-5 pb-20 sm:pb-5">
              <div className="mb-4">
                <div className="flex justify-between text-xs text-muted-foreground mb-2">
                  <span>Question {currentQuestion + 1} of {QUIZ_QUESTIONS.length}</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>

              <h3 className="text-base sm:text-lg font-semibold text-primary mb-4">
                {QUIZ_QUESTIONS[currentQuestion].question}
              </h3>

              <div className="space-y-3">
                {QUIZ_QUESTIONS[currentQuestion].options.map((option, index) => (
                  <button
                    key={index}
                    className={`w-full text-left h-auto py-3 px-4 rounded-lg border transition-colors ${
                      selectedOption === option 
                        ? 'border-violet-500 bg-violet-500/10' 
                        : 'border-border hover:bg-muted/50 hover:border-muted-foreground/30'
                    } ${selectedOption !== null && selectedOption !== option ? 'opacity-50' : ''}`}
                    onClick={() => handleAnswer(option)}
                    disabled={selectedOption !== null}
                  >
                    <span className="text-sm leading-relaxed block break-words">{option.text}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Results */
            <div className="p-5 pb-24 sm:pb-5 overflow-y-auto max-h-[70vh]">
              <div className="text-center mb-6">
                <div className={`w-20 h-20 mx-auto rounded-full bg-gradient-to-br ${result.color} flex items-center justify-center mb-4`}>
                  <span className="text-4xl">{result.emoji}</span>
                </div>
                <h3 className="text-2xl font-bold text-primary">{result.title}</h3>
              </div>

              <div className="bg-muted/30 rounded-xl p-4 mb-6">
                <p className="text-sm text-muted-foreground whitespace-pre-line">
                  {result.description}
                </p>
              </div>

              <Card className="bg-violet-500/10 border-violet-500/30 mb-6">
                <CardContent className="p-4">
                  <p className="text-sm font-medium text-violet-700 dark:text-violet-400 mb-1">📚 Recommended Reading:</p>
                  <p className="text-sm text-muted-foreground">{result.advice}</p>
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={resetQuiz}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Take Again
                </Button>
                <Button 
                  className={`flex-1 bg-gradient-to-r ${result.color} text-white`}
                  onClick={onClose}
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SoulConnectionQuiz;
